import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, Mail, User, FileText, AlertCircle, ExternalLink, Clock, Calendar, Upload, Image as ImageIcon, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { EditableText } from '../components/EditableText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RepairRequest() {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    itemToRepair: '',
    description: '',
    brandModel: '',
    additionalInfo: '',
    timeSlot: '',
    categoryIds: [],
    categoryOther: '',
    oneItemConfirmed: false,
    consent: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryAvailability, setCategoryAvailability] = useState(null);
  
  // Dynamic event settings from admin configuration
  const [eventSettings, setEventSettings] = useState({
    event_date: 'To be announced',
    event_time: '',
    venue_name: 'Peterborough Repair Café',
    venue_url: '',
    default_slot_capacity: 5,
    time_slots: [],
    booking_deadline: null,
    is_booking_closed: false
  });
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Derived values from event settings
  const eventDate = eventSettings.event_date;
  const eventVenue = eventSettings.venue_name;
  const venueUrl = eventSettings.venue_url;
  const allTimeSlots = eventSettings.time_slots.map(slot => ({
    time: slot,
    capacity: eventSettings.default_slot_capacity
  }));
  const bookingDeadline = eventSettings.booking_deadline || '2 days prior to the event';
  const isBookingClosed = !!eventSettings.is_booking_closed;
  const isPostEventLock = !!eventSettings.is_post_event_lock;

  // Fetch event settings, slot availability and categories on component mount
  useEffect(() => {
    fetchEventSettings();
    fetchCategories();
  }, []);

  // If user lands here via `/repair-request#enquiry-form` (e.g. from the
  // Footer / Contact page "Send an enquiry" button), wait for layout to
  // settle then smoothly scroll the form into view.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#enquiry-form') return;
    const id = window.setTimeout(() => {
      const el = document.getElementById('enquiry-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => window.clearTimeout(id);
  }, []);

  // Fetch slot availability when event settings are loaded
  useEffect(() => {
    if (!loadingSettings && eventSettings.time_slots.length > 0) {
      fetchSlotAvailability();
    }
  }, [loadingSettings, eventSettings]);

  const fetchEventSettings = async () => {
    try {
      setLoadingSettings(true);
      // Cache-bust query param + no-store to defeat browser/CDN caching so
      // updates from the admin Schedule tab show up immediately on refresh.
      const response = await axios.get(`${API}/event-settings/public`, {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (response.data) {
        setEventSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching event settings:', error);
      toast.error('Failed to load event information. Using defaults.');
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchSlotAvailability = async () => {
    try {
      setLoadingSlots(true);
      
      // Build query with all slot capacities
      const slotCapacities = allTimeSlots.map(slot => 
        `${encodeURIComponent(slot.time)}:${slot.capacity}`
      ).join(',');
      
      const response = await axios.get(
        `${API}/repair-requests/availability/${encodeURIComponent(eventDate)}?slots=${slotCapacities}`
      );
      
      const availability = response.data.slots || {};

      // Build list of slots with their actual remaining capacity. The
      // backend is the source of truth; if a slot is missing from the
      // response or returns `available: false`, we DO NOT optimistically
      // open it up — that was the production bug that surfaced as
      // "every category shows 54 slots". When the rota is empty for an
      // event, every slot correctly returns capacity = 0.
      const slots = allTimeSlots.map(slot => {
        const slotTime = slot.time;
        const slotInfo = availability[slotTime];

        if (slotInfo) {
          return {
            time: slotTime,
            remaining: slotInfo.remaining,
            capacity: slotInfo.capacity ?? slot.capacity,
            available: slotInfo.available,
          };
        }
        // No backend info → treat the slot as closed. The booking API
        // will reject any attempt anyway, so the form must match.
        return {
          time: slotTime,
          remaining: 0,
          capacity: 0,
          available: false,
        };
      }).filter(slot => slot.available); // Only show available slots
      
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching slot availability:', error);
      // Network/server error → fail safe (no slots offered). The booking
      // API is the source of truth and will reject anyway; matching the
      // form to that behaviour avoids the "shows available but rejects
      // on submit" UX bug surfaced in the P0 incident.
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API}/categories?active_only=true`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchCategoryAvailability = async () => {
    if (!eventDate || eventDate === 'To be announced') return;
    try {
      const response = await axios.get(
        `${API}/repair-requests/slot-availability/${encodeURIComponent(eventDate)}`
      );
      setCategoryAvailability(response.data);
    } catch (error) {
      console.error('Error fetching category availability:', error);
      setCategoryAvailability(null);
    }
  };

  useEffect(() => {
    if (!loadingSettings && eventDate && eventDate !== 'To be announced') {
      fetchCategoryAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSettings, eventDate]);

  useEffect(() => {
    if (formData.categoryIds.length > 0 && eventDate && eventDate !== 'To be announced') {
      fetchCategoryAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.categoryIds]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      timeSlot: value
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter(id => id !== categoryId)
        : [...prev.categoryIds, categoryId]
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Booking deadline guard — prevent submission once the 2-day cutoff has passed.
    if (isBookingClosed) {
      toast.error('Bookings are now closed', {
        description: `The booking deadline (${bookingDeadline}) has passed. Please wait for the next event.`,
        duration: 7000,
      });
      return;
    }

    // Frontend validation - brandModel is now optional
    if (!formData.email || !formData.name || !formData.phone || !formData.itemToRepair || !formData.description ||
        !formData.timeSlot) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Light phone validation — at least 7 digits, allow +, spaces, dashes, parens.
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    // Category validation - now mandatory
    if (!formData.categoryIds || formData.categoryIds.length === 0) {
      toast.error('Please select an item category');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // One item confirmation validation
    if (!formData.oneItemConfirmed) {
      toast.error('Please confirm you are only booking one item');
      return;
    }

    // Consent validation
    if (!formData.consent) {
      toast.error('Please accept the consent agreement to proceed');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data for backend - using correct field names from RepairRequestCreate model
      const requestData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone.trim(),
        item_name: formData.itemToRepair,
        item_description: `${formData.description}${formData.brandModel ? '\n\nBrand/Model: ' + formData.brandModel : ''}${formData.additionalInfo ? '\n\nAdditional Info: ' + formData.additionalInfo : ''}`,
        preferred_event: `${eventDate} - Time: ${formData.timeSlot}`,
        image_urls: imagePreview ? [imagePreview] : [],
        category_ids: formData.categoryIds,
        category_other: formData.categoryOther || null,
        consent: true
      };

      // Submit to backend
      await axios.post(`${API}/repair-requests`, requestData);

      toast.success('Repair request submitted successfully!', {
        description: 'We will review your request and contact you soon.'
      });

      // Reset form and refresh slot availability
      setFormData({
        email: '',
        name: '',
        phone: '',
        itemToRepair: '',
        description: '',
        brandModel: '',
        additionalInfo: '',
        timeSlot: '',
        categoryIds: [],
        categoryOther: '',
        oneItemConfirmed: false,
        consent: false
      });
      setSelectedImage(null);
      setImagePreview(null);
      
      // Refresh available slots after successful submission
      fetchSlotAvailability();
    } catch (error) {
      console.error('Submission error:', error);
      
      // Check for duplicate submission or slot full error
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data.detail || 'Unable to submit request.';
        toast.error('Submission blocked', {
          description: errorMessage,
          duration: 6000
        });
        
        // If slot is full, refresh availability to update UI
        if (errorMessage.includes('fully booked')) {
          fetchSlotAvailability();
        }
      } else {
        toast.error('Failed to submit request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-[#193460] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">
            <EditableText contentKey="repair.hero.title" as="span">Submit a Repair Request</EditableText>
          </h1>
          <p className="text-xl text-gray-200">
            <EditableText contentKey="repair.hero.subtitle" as="span">Peterborough Repair Café</EditableText> - {eventVenue}
          </p>
        </div>
      </section>

      {/* Important Information Section */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Important Notice Alert */}
          <Alert className="mb-8 border-2 border-[#E87B6A] bg-orange-50">
            <AlertCircle className="h-5 w-5 text-[#E87B6A]" />
            <AlertTitle className="text-lg font-bold text-[#193460]">
              <EditableText contentKey="repair.notice.title" as="span">Please Read All Information Before Filling Out the Form</EditableText>
            </AlertTitle>
            <AlertDescription className="text-gray-700 mt-2">
              <p className="font-semibold">
                <EditableText contentKey="repair.notice.line1" as="span">WE GET BACK TO EVERYONE WHO FILLS IN THIS FORM WITH A VALID EMAIL</EditableText>
              </p>
              <p className="mt-1">
                <EditableText contentKey="repair.notice.line2" as="span">IF YOU DO NOT GET A REPLY, PLEASE CHECK YOUR SPAM. If you still haven't heard from us, please get in touch via our Contact page before the booking date.</EditableText>{' '}
                <Link to="/contact" className="text-[#E87B6A] hover:underline font-semibold">Open Contact form →</Link>
              </p>
            </AlertDescription>
          </Alert>

          {/* Post-event 5-day cool-off banner. Takes precedence over the
              regular "closed" banner because the message and reason
              are different (post-event paperwork vs. pre-event lead-time). */}
          {isPostEventLock && (
            <Alert
              data-testid="repair-post-event-lock-banner"
              className="mb-8 border-2 border-amber-500 bg-amber-50"
            >
              <AlertCircle className="h-5 w-5 text-amber-700" />
              <AlertTitle className="text-lg font-bold text-amber-900">
                Repair requests are temporarily closed
              </AlertTitle>
              <AlertDescription className="text-amber-900 mt-2">
                Repair requests are temporarily closed while we process the most recent
                event. Bookings will reopen shortly for the next Repair Café session.
                {eventSettings.post_event_lock_until && (
                  <span className="block mt-2 text-sm text-amber-800">
                    Expected to reopen on or after <strong>{eventSettings.post_event_lock_until}</strong>.
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Bookings closed banner — shown once we're inside the 2-day cutoff window. */}
          {isBookingClosed && !isPostEventLock && (
            <Alert
              data-testid="repair-bookings-closed-banner"
              className="mb-8 border-2 border-red-500 bg-red-50"
            >
              <AlertCircle className="h-5 w-5 text-red-600" />
              <AlertTitle className="text-lg font-bold text-red-800">
                Bookings for the next event are now closed
              </AlertTitle>
              <AlertDescription className="text-red-900 mt-2">
                The booking deadline ({bookingDeadline}) has passed. We close the form
                <strong> 2 days before each repair café</strong> so volunteers have time to
                review your details. Please check back after the event for the next available date,
                or get in touch via our{' '}
                <Link to="/contact" className="underline font-semibold">Contact page</Link>.
              </AlertDescription>
            </Alert>
          )}

          {/* Main Information Card */}
          <Card className="mb-8 border-2 border-gray-200">
            <CardContent className="p-8 space-y-6">
              {/* Welcome Message */}
              <div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  <EditableText contentKey="repair.welcome" as="span" multiline>Hello and welcome to Peterborough Repair Café UK, part of a global movement to reduce waste, strengthen communities, and share skills. We fix portable household items FOR FREE (donations are welcome!).</EditableText>
                </p>
              </div>

              {/* Next Event */}
              <div className="bg-[#193460] text-white p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Calendar className="w-6 h-6 mr-2" />
                  <EditableText contentKey="repair.nextevent.title" as="span">Our Next Repair Café Session</EditableText>
                </h3>
                <p className="text-lg mb-2">
                  <strong>{eventDate}</strong> at <strong>{eventVenue}</strong>
                </p>
                <a 
                  href={venueUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#E87B6A] hover:text-white transition-colors inline-flex items-center"
                >
                  <EditableText contentKey="repair.nextevent.viewvenue" as="span">View venue website</EditableText> <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>

              {/* Form Purpose */}
              <div>
                <p className="text-gray-700 leading-relaxed">
                  <EditableText contentKey="repair.formpurpose" as="span" multiline>Please fill in this form if you have an item for repair that you would like to bring along to one of our volunteers.</EditableText>
                </p>
              </div>

              {/* What We Can Fix */}
              <div>
                <h3 className="text-xl font-bold text-[#193460] mb-4">
                  <EditableText contentKey="repair.canfix.title" as="span">What We Can Fix</EditableText>
                </h3>
                <p className="text-gray-700 mb-3">
                  <EditableText contentKey="repair.canfix.intro" as="span" multiline>There is a limit to what we will attempt to fix; our slots are 45 minutes long. We do functional repairs of portable household items (we are not The Repair Shop from TV!). We can look at things like:</EditableText>
                </p>
                <ul className="space-y-2 text-gray-700 ml-6">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span><EditableText contentKey="repair.canfix.b1" as="span">Simple clothing repairs like tears</EditableText></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span><EditableText contentKey="repair.canfix.b2" as="span">Small electrical appliances like radios and vacuum cleaners</EditableText></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span><EditableText contentKey="repair.canfix.b3" as="span">Tools that need sharpening or repairing (note: there is an age limit of 25 for knife sharpening)</EditableText></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span><EditableText contentKey="repair.canfix.b4" as="span" multiline>Depending on the location and volunteer availability, we might also be able to do basic bike repairs and maintenance, fix toys, or do simple woodwork repairs on portable furniture</EditableText></span>
                  </li>
                </ul>
              </div>

              {/* What We Cannot Fix */}
              <div className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-3">
                  <EditableText contentKey="repair.cannotfix.title" as="span">What We Cannot Fix</EditableText>
                </h3>
                <p className="text-gray-700 mb-2">
                  <EditableText contentKey="repair.cannotfix.headline" as="span">If it can't be carried in, we can't fix it!</EditableText>
                </p>
                <p className="text-gray-700">
                  <EditableText contentKey="repair.cannotfix.body" as="span" multiline>We cannot fix large items such as washing machines, fridges, cars, or large TVs. We also don't do specialist repairs for things like mobile phones, or complicated restoration projects on furniture, or family heirlooms.</EditableText>
                </p>
                <p className="text-gray-700 mt-3">
                  <EditableText contentKey="repair.cannotfix.outro" as="span" multiline>If you are unsure, pop some details on the form below and one of the team will get back to you.</EditableText>
                </p>
              </div>

              {/* One Item Rule */}
              <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-300">
                <h3 className="text-xl font-bold text-yellow-900 mb-3">
                  <EditableText contentKey="repair.oneitem.title" as="span">One Item Per Person Policy</EditableText>
                </h3>
                <p className="text-gray-700 mb-2">
                  <EditableText contentKey="repair.oneitem.body1" as="span" multiline>We only allow booking of ONE ITEM per person per repair café, this is to give as many people as possible a chance to use this opportunity.</EditableText>
                </p>
                <p className="text-gray-700">
                  <EditableText contentKey="repair.oneitem.body2" as="span" multiline>You could bring another item with you, and if there is time, we might be able to look at it. Please do not try to book for more than one item.</EditableText>
                </p>
              </div>

              {/* Important Deadlines and Information */}
              <div>
                <h3 className="text-xl font-bold text-[#193460] mb-4">
                  <EditableText contentKey="repair.important.title" as="span">Important Information</EditableText>
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Booking Deadline:</strong> Please submit your repair request by <strong>{bookingDeadline}</strong>
                      {eventSettings.booking_deadline && (
                        <span className="text-sm text-gray-600"> &nbsp;(2 days before the event)</span>
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <EditableText contentKey="repair.important.b2" as="span" multiline>It helps us if you can confirm your appointment by replying to the email sent from the repairer</EditableText>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <EditableText contentKey="repair.important.b3" as="span" multiline>You are more likely to get a successful repair if you interact with the repairer before the session (for example, send photos, discuss parts you might need to purchase, answer questions about the item)</EditableText>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <EditableText contentKey="repair.important.b4" as="span" multiline>On the day: You must stay with the repairer - the idea is that you discuss the repair together</EditableText>
                    </span>
                  </li>
                </ul>
              </div>

              {/* Privacy Notice */}
              <div className="border-l-4 border-[#193460] pl-4 py-2 bg-blue-50">
                <p className="text-sm text-gray-700">
                  <EditableText contentKey="repair.privacy" as="span" multiline>By filling in this form you give Peterborough Repair Café permission to share your email address with volunteers to enable them to contact you about the item to be repaired.</EditableText>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Repair Request Form */}
          <Card id="enquiry-form" className="border-2 border-gray-200 shadow-xl scroll-mt-24">
            <CardHeader>
              <CardTitle className="text-3xl text-[#193460]">
                <EditableText contentKey="repair.form.title" as="span">Request Form</EditableText>
              </CardTitle>
              <CardDescription className="text-base">
                <EditableText contentKey="repair.form.description" as="span" multiline>Fill out the form below to request a repair. All fields marked with * are required.</EditableText>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                    required
                  />
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g. 07700 900123"
                    className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                    data-testid="repair-phone-input"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    So a volunteer can contact you if we need more information about your item.
                  </p>
                </div>

                {/* Item to Repair */}
                <div>
                  <Label htmlFor="itemToRepair" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Item to Repair *
                  </Label>
                  <Input
                    id="itemToRepair"
                    name="itemToRepair"
                    type="text"
                    value={formData.itemToRepair}
                    onChange={handleInputChange}
                    placeholder="E.g., Toaster, Bicycle, Lamp, etc."
                    className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                    required
                  />
                </div>

                {/* Short Description of Fault */}
                <div>
                  <Label htmlFor="description" className="text-base font-semibold text-[#193460] mb-2 block">
                    Short Description of Fault *
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please briefly describe what's wrong with your item..."
                    rows={4}
                    className="border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300 resize-none"
                    required
                  />
                </div>

                {/* Brand/Model Number */}
                <div>
                  <Label htmlFor="brandModel" className="text-base font-semibold text-[#193460] mb-2 block">
                    Brand/Model Number (Optional)
                  </Label>
                  <Input
                    id="brandModel"
                    name="brandModel"
                    type="text"
                    value={formData.brandModel}
                    onChange={handleInputChange}
                    placeholder="E.g., Philips HD2581, Bosch PKP 3.6, etc."
                    className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                  />
                </div>

                {/* Additional Information */}
                <div>
                  <Label htmlFor="additionalInfo" className="text-base font-semibold text-[#193460] mb-2 block">
                    Anything We Should Know About You (Optional)
                  </Label>
                  <Textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    placeholder="E.g., mobility issues, assistance needed, special requirements..."
                    rows={3}
                    className="border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Let us know if you have any mobility issues or need special assistance
                  </p>
                </div>

                {/* Category Selection */}
                {/* Item Category - Now mandatory dropdown */}
                <div>
                  <Label htmlFor="category" className="text-base font-semibold text-[#193460] mb-2 block">
                    Item Category <span className="text-red-600">*</span>
                  </Label>
                  <p className="text-sm text-gray-600 mb-2">Please choose the closest category</p>
                  <Select 
                    value={formData.categoryIds[0] || ''} 
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      categoryIds: [value]
                    }))}
                    required
                  >
                    <SelectTrigger className="h-12 border-2 border-gray-300">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingCategories ? (
                        <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                      ) : (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Slot Selection with Category Availability */}
                <div>
                  <Label htmlFor="timeSlot" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Preferred Time Slot *
                  </Label>
                  {loadingSlots ? (
                    <div className="h-12 border-2 border-gray-300 rounded-md flex items-center justify-center">
                      <span className="text-gray-500">Loading available slots...</span>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-md">
                      <p className="text-red-800 font-semibold">All time slots are fully booked.</p>
                      <p className="text-red-700 text-sm mt-1">Please check back later or contact us for assistance.</p>
                    </div>
                  ) : (
                    <>
                      <Select value={formData.timeSlot} onValueChange={handleSelectChange} required>
                        <SelectTrigger className="h-12 border-2 border-gray-300 focus:border-[#E87B6A]">
                          <SelectValue placeholder="Select your preferred time" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSlots.map((slot) => {
                            let spacesLeft = slot.remaining;

                            // Dynamic per-category availability (keyed by category_id)
                            if (categoryAvailability && formData.categoryIds.length > 0) {
                              const selectedCategoryId = formData.categoryIds[0];
                              const normalizedSlotTime =
                                slot.time.match(/(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2})/)?.[0] || slot.time;
                              const slotData = categoryAvailability.slots?.find(
                                (s) => s.time_slot === normalizedSlotTime
                              );
                              const info = slotData?.categories?.[selectedCategoryId];
                              if (info) spacesLeft = info.available;
                            }

                            const displayText = `${slot.time} (${spacesLeft} ${spacesLeft === 1 ? 'space' : 'spaces'} left)`;

                            return (
                              <SelectItem key={slot.time} value={slot.time}>
                                {displayText}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>

                      {/* Category Availability Info — dynamic */}
                      {categoryAvailability && formData.categoryIds.length > 0 && (() => {
                        const selectedCategoryId = formData.categoryIds[0];
                        const totals = categoryAvailability.total_capacity?.[selectedCategoryId];
                        if (!totals) return null;
                        return (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-800 font-medium mb-1">
                              Category Availability:
                            </p>
                            <p className="text-xs text-blue-700">
                              ✓ {totals.name}: {totals.total} total slots available across all times
                            </p>
                          </div>
                        );
                      })()}

                      <p className="text-sm text-gray-600 mt-2">
                        {formData.categoryIds.length > 0 ?
                          'Slots shown are available for your selected category. Choose your preferred time.' :
                          'Time slots shown are based on current availability. Slots fill up quickly!'
                        }
                      </p>
                    </>
                  )}
                </div>

                {/* Image Upload */}
                <div>
                  <Label htmlFor="image" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Upload Photo of Item (Optional)
                  </Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#E87B6A] transition-colors duration-300">
                    {imagePreview ? (
                      <div className="space-y-4">
                        <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-lg shadow-md" />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview(null);
                          }}
                          className="border-2 border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
                        >
                          Remove Image
                        </Button>
                      </div>
                    ) : (
                      <label htmlFor="image" className="cursor-pointer block">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-600 mb-2">Click to upload an image</p>
                        <p className="text-sm text-gray-500">PNG, JPG up to 10MB</p>
                        <input
                          id="image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* One Item Confirmation */}
                <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
                  <Checkbox
                    id="oneItemConfirmed"
                    checked={formData.oneItemConfirmed}
                    onCheckedChange={(checked) => handleCheckboxChange('oneItemConfirmed', checked)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor="oneItemConfirmed" 
                      className="text-sm text-gray-700 cursor-pointer leading-relaxed font-semibold"
                    >
                      I confirm I have only tried to book one item *
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      (It only adds to our admin load if you try to book more than one, as we then have to contact you to ask which one you want to bring)
                    </p>
                  </div>
                </div>

                {/* General Consent Checkbox */}
                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => handleCheckboxChange('consent', checked)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor="consent" 
                      className="text-sm text-gray-700 cursor-pointer leading-relaxed"
                    >
                      I consent to Peterborough Repair Cafe UK storing my information and contacting me about my repair request. I understand that my data will be handled in accordance with data protection regulations. *
                    </Label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    disabled={submitting || isBookingClosed}
                    data-testid="repair-submit-btn"
                    size="lg" 
                    className="w-full bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold h-14 text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <CheckCircle2 className="mr-2 w-5 h-5" />
                    {isPostEventLock
                      ? 'Bookings Reopen Soon — Try Again Later'
                      : isBookingClosed
                      ? 'Bookings Closed for the Next Event'
                      : (submitting ? 'Submitting...' : 'Submit Repair Request')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mt-8 border-2 border-[#193460] bg-blue-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#193460] text-lg mb-3 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <EditableText contentKey="repair.next.title" as="span">What Happens Next?</EditableText>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#E87B6A] font-bold mr-2">1.</span>
                  <span><EditableText contentKey="repair.next.s1" as="span">Our volunteer team will review your repair request</EditableText></span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#E87B6A] font-bold mr-2">2.</span>
                  <span><EditableText contentKey="repair.next.s2" as="span">We'll contact you via email or phone to confirm details</EditableText></span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#E87B6A] font-bold mr-2">3.</span>
                  <span><EditableText contentKey="repair.next.s3" as="span">Bring your item to the next monthly repair cafe event</EditableText></span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#E87B6A] font-bold mr-2">4.</span>
                  <span><EditableText contentKey="repair.next.s4" as="span">Our volunteers will attempt the repair on the day - completely free!</EditableText></span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
